import { useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { BarChart3, BellRing, ClipboardList, FileStack, Radar, Send, Users } from "lucide-react";
import { toast } from "sonner";
import { AeroImg } from "@/components/AeroImg";
import { Footer } from "@/components/Footer";
import { Header } from "@/components/Header";
import { RAHALTY_PAGE as R } from "@/data";
import { useLang } from "@/i18n";
import { API_BASE as API } from "@/config";

const EASE = [0.16, 1, 0.3, 1];
const ICONS = { ClipboardList, Radar, FileStack, Users, BarChart3, BellRing };

const inputCls =
  "w-full rounded-xl border border-blue-500/20 bg-[#081020] px-4 py-3 text-sm text-white placeholder:text-slate-500 outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 transition-all duration-200";

export default function RahaltyPage() {
  const { lang } = useLang();
  const [form, setForm] = useState({ company_name: "", contact_name: "", contact_email: "", contact_phone: "", monthly_volume: R.volumes.ar[0], notes: "" });
  const [sending, setSending] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      const { data } = await axios.post(`${API}/rahalty/trial`, { ...form, language: lang });
      toast.success(`${R.f_success[lang]}: ${data.ref}`);
      setForm({ company_name: "", contact_name: "", contact_email: "", contact_phone: "", monthly_volume: R.volumes[lang][0], notes: "" });
    } catch {
      toast.error(R.f_error[lang]);
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#060B14]" data-testid="rahalty-page" data-always-dark>
      <Header />
      <main>
        <section className="relative pt-40 pb-20 overflow-hidden grain">
          <div className="absolute inset-0 grid-lines opacity-50" />
          <div className="absolute -top-32 start-1/4 w-[480px] h-[480px] rounded-full bg-blue-600/15 blur-[120px]" />
          <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              <div>
                <motion.span
                  initial={{ opacity: 0, y: 16 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.7, ease: EASE }}
                  className="inline-block rounded-full border border-blue-500/30 bg-blue-950/40 px-4 py-2 font-mono-tag text-[11px] tracking-widest text-blue-300"
                  data-testid="rahalty-page-badge"
                >
                  {R.hero_badge[lang]}
                </motion.span>
                <h1 className="mt-6 text-4xl sm:text-5xl font-extrabold leading-[1.2] tracking-tight text-white" data-testid="rahalty-page-title">
                  <span className="block overflow-hidden pb-1">
                    <motion.span className="block" initial={{ y: "115%" }} animate={{ y: 0 }} transition={{ duration: 1, delay: 0.2, ease: EASE }}>
                      {R.hero_title[lang]}
                    </motion.span>
                  </span>
                </h1>
                <motion.p
                  initial={{ opacity: 0, y: 18 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.8, delay: 0.6, ease: EASE }}
                  className="mt-6 max-w-xl text-base sm:text-lg leading-relaxed text-slate-300"
                >
                  {R.hero_sub[lang]}
                </motion.p>
                <motion.button
                  initial={{ opacity: 0, y: 18 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.8, delay: 0.8, ease: EASE }}
                  data-testid="rahalty-page-trial-cta"
                  onClick={() => document.getElementById("rahalty-trial")?.scrollIntoView({ behavior: "smooth" })}
                  className="mt-9 rounded-full bg-blue-600 px-8 py-4 text-base font-bold text-white hover:bg-blue-500 transition-all duration-300 shadow-[0_0_36px_rgba(37,99,235,0.5)] hover:-translate-y-0.5"
                >
                  {R.hero_cta[lang]}
                </motion.button>
              </div>
              <motion.div
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 1.1, delay: 0.3, ease: EASE }}
                className="flex justify-center"
              >
                <img src="/assets/brand/logo-rahalty.png" alt="Rahalty logo" className="w-64 sm:w-80 lg:w-96 h-auto animate-float drop-shadow-[0_24px_60px_rgba(0,0,0,0.65)]" />
              </motion.div>
            </div>
          </div>
        </section>

        <section className="relative py-20 border-t border-blue-500/10" data-testid="rahalty-features-section">
          <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
            <span className="font-mono-tag text-xs tracking-[0.3em] uppercase text-blue-400">{R.features_eyebrow[lang]}</span>
            <h2 className="mt-4 mb-12 text-3xl sm:text-4xl font-extrabold tracking-tight text-white">{R.features_title[lang]}</h2>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 lg:gap-5">
              {R.features.map((f, i) => {
                const Icon = ICONS[f.icon] || Radar;
                return (
                  <motion.div
                    key={f.title_en}
                    data-testid={`rahalty-feature-${i}`}
                    initial={{ opacity: 0, y: 28 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, margin: "-60px" }}
                    transition={{ duration: 0.7, delay: (i % 3) * 0.1, ease: EASE }}
                    className="rounded-2xl border border-blue-500/15 bg-[#0B1526] p-6 hover:border-blue-500/40 hover:-translate-y-1 transition-all duration-300"
                  >
                    <div className="rounded-xl bg-blue-600/15 border border-blue-500/25 p-3 text-blue-400 w-fit"><Icon size={20} /></div>
                    <h3 className="mt-4 text-lg font-bold text-white">{f[`title_${lang}`]}</h3>
                    <p className="mt-2 text-sm leading-relaxed text-slate-400">{f[`desc_${lang}`]}</p>
                  </motion.div>
                );
              })}
            </div>
          </div>
        </section>

        <section className="relative py-20 border-t border-blue-500/10 bg-[#081020]" data-testid="rahalty-shots-section">
          <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
            <div className="flex items-baseline gap-3 flex-wrap">
              <span className="font-mono-tag text-xs tracking-[0.3em] uppercase text-blue-400">{R.shots_eyebrow[lang]}</span>
              <span className="text-xs text-slate-500">{R.shots_note[lang]}</span>
            </div>
            <div className="mt-10 grid md:grid-cols-3 gap-5">
              {R.shots.map((s, i) => (
                <motion.figure
                  key={s.img}
                  data-testid={`rahalty-shot-${i}`}
                  initial={{ opacity: 0, y: 28 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-60px" }}
                  transition={{ duration: 0.75, delay: i * 0.12, ease: EASE }}
                  className="group"
                >
                  <div className="overflow-hidden rounded-2xl border border-blue-500/20 shadow-[0_24px_60px_rgba(2,6,17,0.5)]">
                    <AeroImg img={s.img} alt={s.cap_en} className="aspect-[4/3] w-full object-cover group-hover:scale-105 transition-transform duration-700" />
                  </div>
                  <figcaption className="mt-3 text-sm font-semibold text-slate-300">{s[`cap_${lang}`]}</figcaption>
                </motion.figure>
              ))}
            </div>
          </div>
        </section>

        <section id="rahalty-trial" className="relative py-20 lg:py-28 border-t border-blue-500/10" data-testid="rahalty-trial-section">
          <div className="mx-auto max-w-3xl px-4 sm:px-6 lg:px-8">
            <motion.div
              initial={{ opacity: 0, y: 28 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{ duration: 0.8, ease: EASE }}
            >
              <h2 className="text-3xl sm:text-4xl font-extrabold tracking-tight text-white">{R.trial_title[lang]}</h2>
              <p className="mt-3 text-base text-slate-400">{R.trial_sub[lang]}</p>
              <form onSubmit={submit} data-testid="rahalty-trial-form" className="mt-9 rounded-3xl border border-blue-500/20 bg-[#0B1526] p-6 sm:p-9 shadow-[0_30px_80px_rgba(2,6,17,0.5)]">
                <div className="grid sm:grid-cols-2 gap-4">
                  <label className="block">
                    <span className="mb-1.5 block text-xs font-semibold text-slate-300">{R.f_company[lang]}</span>
                    <input data-testid="trial-company-input" value={form.company_name} onChange={set("company_name")} className={inputCls} required />
                  </label>
                  <label className="block">
                    <span className="mb-1.5 block text-xs font-semibold text-slate-300">{R.f_name[lang]}</span>
                    <input data-testid="trial-name-input" value={form.contact_name} onChange={set("contact_name")} className={inputCls} required />
                  </label>
                  <label className="block">
                    <span className="mb-1.5 block text-xs font-semibold text-slate-300">{R.f_email[lang]}</span>
                    <input data-testid="trial-email-input" type="email" value={form.contact_email} onChange={set("contact_email")} className={inputCls} required dir="ltr" placeholder="ops@airline.com" />
                  </label>
                  <label className="block">
                    <span className="mb-1.5 block text-xs font-semibold text-slate-300">{R.f_phone[lang]}</span>
                    <input data-testid="trial-phone-input" type="tel" value={form.contact_phone} onChange={set("contact_phone")} className={inputCls} dir="ltr" placeholder="+218 ..." />
                  </label>
                  <label className="block sm:col-span-2">
                    <span className="mb-1.5 block text-xs font-semibold text-slate-300">{R.f_volume[lang]}</span>
                    <select data-testid="trial-volume-select" value={form.monthly_volume} onChange={set("monthly_volume")} className={inputCls}>
                      {R.volumes[lang].map((v) => <option key={v} value={v}>{v}</option>)}
                    </select>
                  </label>
                  <label className="block sm:col-span-2">
                    <span className="mb-1.5 block text-xs font-semibold text-slate-300">{R.f_notes[lang]}</span>
                    <textarea data-testid="trial-notes-textarea" value={form.notes} onChange={set("notes")} rows={4} className={`${inputCls} resize-none`} />
                  </label>
                </div>
                <button
                  data-testid="trial-form-submit-button"
                  type="submit"
                  disabled={sending}
                  className="mt-7 inline-flex items-center gap-2 rounded-full bg-blue-600 px-8 py-4 text-sm font-bold text-white hover:bg-blue-500 disabled:opacity-60 transition-all duration-200 shadow-[0_0_28px_rgba(37,99,235,0.45)]"
                >
                  <Send size={16} className="rtl:-scale-x-100" />
                  {sending ? R.f_submitting[lang] : R.f_submit[lang]}
                </button>
              </form>
            </motion.div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}
